"use client";

// BatchForgetSheet — forgetting many at once, asked twice in the same place.
//
// The dashboard's select mode hands this sheet a pile of pages (inside one
// project) or a pile of whole projects. Opening it is the first ask: it fetches
// the same preview a chat gets (innernet_forget with `names`, or
// innernet_forget_project with a list) and shows EVERY item that would go, what
// it holds, and plainly that there is no undo. The pill in the fixed bottom-right
// slot is the second ask: it sends the preview's own code back with the same
// selection, so what is deleted is exactly the list that was shown. If anything
// in the selection changed in between, the server shows the new list instead.
//
// One implementation behind it (lib/mcp/forget.ts and lib/mcp/handlers.ts, via
// POST /api/os/forget).
//
// It is the sheet, small (law_sheet_rises): the title and one meta line in its
// head, the list in its body (it scrolls inside itself so the blast-radius line
// stays in view above the foot), and the foot is the whole decision, "keep
// them" quiet on the left and the pill in the corner.

import { useCallback, useEffect, useState } from "react";
import EtherModal from "../ether/EtherModal";
import { useSheetLeave } from "../../lib/os/useSheetLeave";
import { PillButton } from "../ui";
import "./pick.css";

export type BatchForgetTarget =
  | { kind: "pages"; projectId: string; names: string[] }
  | { kind: "projects"; projectIds: string[] };

interface Holds { dimensions?: number; nodes?: number; commits?: number }
interface Preview {
  confirmation_required?: boolean;
  stale_or_wrong?: boolean;
  forgetting?: {
    count: number;
    where?: string;
    names?: string[];
    projects?: { slug: string; name: string; holds: Holds }[];
    holds?: Holds;
  };
  stays?: number;
  confirm_with?: string;
  ok?: boolean;
  error?: string;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export default function BatchForgetSheet({
  target,
  onClose,
  onForgotten,
}: {
  target: BatchForgetTarget;
  onClose: () => void;
  onForgotten: (count: number) => void;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ask = useCallback(
    async (confirm?: string) => {
      const body =
        target.kind === "pages"
          ? { kind: "page", project_id: target.projectId, names: target.names, confirm }
          : { kind: "projects", project_ids: target.projectIds, confirm };
      const res = await fetch("/api/os/forget", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return (await res.json()) as Preview;
    },
    [target],
  );

  // the first ask: exactly what would go.
  useEffect(() => {
    let live = true;
    void ask()
      .then((p) => {
        if (!live) return;
        if (p.error) setErr(p.error);
        else setPreview(p);
      })
      .catch(() => live && setErr("couldn't reach your memory. try again."));
    return () => { live = false; };
  }, [ask]);

  // the second ask: the code from the preview, with the same selection, deletes.
  const forget = async () => {
    if (!preview?.confirm_with || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const out = await ask(preview.confirm_with);
      if (out.ok) {
        onForgotten(preview.forgetting?.count ?? 0);
        return;
      }
      if (out.confirmation_required) {
        setPreview(out);
        setErr("something changed since this opened. here is the list as it stands now.");
      } else {
        setErr(out.error ?? "that didn't go through. nothing was forgotten.");
      }
    } catch {
      setErr("that didn't go through. nothing was forgotten.");
    } finally {
      setBusy(false);
    }
  };

  const f = preview?.forgetting;
  const count = f?.count ?? (target.kind === "pages" ? target.names.length : target.projectIds.length);
  const noun = target.kind === "pages" ? "page" : "project";
  const where = target.kind === "pages" && f?.where ? f.where : null;
  const blast =
    target.kind === "pages"
      ? `only these ${plural(count, "page")} go${typeof preview?.stays === "number" ? `. ${plural(preview.stays, "other page")} stay` : ""}.`
      : `every page, cell and commit inside ${count === 1 ? "it" : "them"} goes too.`;

  // the list loads after the sheet opens and a sheet is measured once: hold the
  // room the rows will take (the count is known before the names are)
  const reserve = `${Math.min(count * 2.9, 22) + (target.kind === "pages" ? 1.6 : 0)}rem`;
  // mounted by its caller only while it is wanted: it sinks, then the caller hears
  const { open, leave } = useSheetLeave(onClose);

  return (
    <EtherModal
      open={open}
      onClose={leave}
      labelledBy="bf-title"
      size="sm"
      height="fit"
      header={
        <>
          <h2 id="bf-title" className="eth-title">forget {plural(count, noun)}?</h2>
          <p className="eth-meta">this is permanent. there is no undo.</p>
        </>
      }
      footStart={
        <button type="button" className="quiet-link" onClick={leave} disabled={busy}>
          keep them
        </button>
      }
      footer={
        <PillButton onClick={() => void forget()} disabled={busy || !preview?.confirm_with}>
          {busy ? "forgetting…" : `forget ${plural(count, noun)}`}
        </PillButton>
      }
    >
      <div className="bf-body" style={{ minHeight: reserve }}>
        {where ? <p className="fg-where">in {where}</p> : null}
        {!f && !err && <p className="fg-wait">finding exactly what would go…</p>}
        {f?.names && (
          <ol className="bf-list" aria-label={`the ${noun}s that would be forgotten`}>
            {f.names.map((n) => (
              <li key={n} className="bf-item"><span className="bf-name">{n}</span></li>
            ))}
          </ol>
        )}
        {f?.projects && (
          <ol className="bf-list" aria-label="the projects that would be forgotten">
            {f.projects.map((p) => (
              <li key={p.slug} className="bf-item">
                <span className="bf-name">{p.name}</span>
                <span className="bf-meta">
                  {plural(p.holds.dimensions ?? 0, "page")} · {plural(p.holds.nodes ?? 0, "cell")}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
      <p className="rt-says" data-warn={err ? "" : undefined}>
        {err ?? (preview?.confirm_with ? blast : "")}
      </p>
    </EtherModal>
  );
}
